import { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, ChefHat, Loader2, ChevronRight } from 'lucide-react';
import { TYRKYS, ORANZOVA, styly, Nadpis, Hledani, Posun, nedavne, bezDiakritiky } from './ui';
import SmerniceSeznam from './SmerniceSeznam';
import { ReceptySeznam, ReceptDetail, Nahled } from './Recepty';

// Řízená dokumentace — sekce mobilního Přehledu. Obsluha tu najde všechno
// závazné, podle čeho pracuje: přiřazené směrnice a (podle kariéry) recepty.
// Podstránky jsou v adrese (`druh`, `recept`, `smernice`), takže systémové
// Zpět vrací z detailu do seznamu. `z=1` = přišel jsem zevnitř Dokumentace,
// šipka zpět pak jde po historii; z notifikace otevře rodiče.

const DRUHY = {
    smernice: { nazev: 'Směrnice', Icon: ShieldCheck, barva: TYRKYS, jednotky: ['dokument', 'dokumenty', 'dokumentů'], cekani: 'k potvrzení' },
    recepty:  { nazev: 'Recepty', Icon: ChefHat, barva: ORANZOVA, jednotky: ['recept', 'recepty', 'receptů'], cekani: n => n === 1 ? 'změna' : n < 5 ? 'změny' : 'změn' },
};
const sklonuj = (n, [jeden, dva, pet]) => `${n} ${n === 1 ? jeden : n > 1 && n < 5 ? dva : pet}`;

export default function Dokumentace({ isDark, user, navParams, onNavigate, onZmena }) {
    const druh = navParams?.druh;
    const [prehled, setPrehled] = useState(null);

    const nactiPrehled = useCallback(() => {
        fetch('/api/me/dokumentace').then(r => r.ok ? r.json() : null).then(setPrehled).catch(() => setPrehled({ druhy: [], k_potvrzeni: [] }));
    }, []);
    useEffect(() => { nactiPrehled(); }, [nactiPrehled, druh]);

    const jdi = (params) => onNavigate?.('prehled', { tab: 'dokumentace', z: 1, ...params });
    const zpetNa = (rodic) => () => (navParams?.z ? window.history.back() : onNavigate?.('prehled', { tab: 'dokumentace', ...rodic }));
    const poZmene = () => { nactiPrehled(); onZmena?.(); };

    if (druh === 'recepty' && navParams?.recept) {
        return <ReceptDetail key={navParams.recept} id={navParams.recept} isDark={isDark} onZpet={zpetNa({ druh: 'recepty' })}
            onOtevrit={id => jdi({ druh: 'recepty', recept: id })} onPotvrzeno={poZmene} />;
    }
    if (druh === 'recepty') {
        return <ReceptySeznam isDark={isDark} onZpet={zpetNa({})} onOtevrit={id => jdi({ druh: 'recepty', recept: id })} />;
    }
    if (druh === 'smernice') {
        return <SmerniceSeznam isDark={isDark} user={user} otevritId={navParams?.smernice} onZpet={zpetNa({})} onPotvrzeno={poZmene} />;
    }
    return <Domov isDark={isDark} prehled={prehled} jdi={jdi} />;
}

function Domov({ isDark, prehled, jdi }) {
    const s = styly(isDark);
    const [q, setQ] = useState('');
    const [vysledky, setVysledky] = useState(null);
    const maRecepty = prehled?.druhy?.some(d => d.kod === 'recepty');

    // Hledání přes všechny druhy: směrnice filtrujeme tady, recepty hledá server
    useEffect(() => {
        const dotaz = q.trim();
        if (!dotaz) { setVysledky(null); return; }
        const t = setTimeout(async () => {
            const [smernice, recepty] = await Promise.all([
                fetch('/api/me/dokumentace/smernice').then(r => r.ok ? r.json() : []).catch(() => []),
                maRecepty ? fetch(`/api/me/dokumentace/recepty?q=${encodeURIComponent(dotaz)}`).then(r => r.ok ? r.json() : []).catch(() => []) : [],
            ]);
            const h = bezDiakritiky(dotaz);
            setVysledky({
                smernice: smernice.filter(x => bezDiakritiky(`${x.nazev} ${x.cislo_dokumentu || ''} ${x.kategorie?.nazev || ''}`).includes(h)),
                recepty,
            });
        }, 250);
        return () => clearTimeout(t);
    }, [q, maRecepty]);

    if (!prehled) {
        return <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;
    }

    const posledni = maRecepty ? nedavne() : [];

    return (
        <Posun>
            <Hledani value={q} onChange={setQ} isDark={isDark}
                placeholder={maRecepty ? 'Hledat ve směrnicích a receptech…' : 'Hledat ve směrnicích…'} />

            {vysledky ? (
                <div>
                    {vysledky.smernice.length === 0 && vysledky.recepty.length === 0 && (
                        <p className="text-sm text-center" style={{ color: 'var(--text-muted)', marginTop: 32 }}>Nic jsem nenašel.</p>
                    )}
                    {vysledky.smernice.length > 0 && <>
                        <Nadpis>Směrnice ({vysledky.smernice.length})</Nadpis>
                        {vysledky.smernice.map(x => (
                            <Polozka key={`s${x.id}`} isDark={isDark} Icon={ShieldCheck} barva={TYRKYS} nazev={x.nazev}
                                popisek={[x.kategorie?.nazev, x.cislo_dokumentu].filter(Boolean).join(' · ')}
                                onClick={() => jdi({ druh: 'smernice', smernice: x.id })} />
                        ))}
                    </>}
                    {vysledky.recepty.length > 0 && <>
                        <Nadpis>Recepty ({vysledky.recepty.length})</Nadpis>
                        {vysledky.recepty.slice(0, 30).map(r => (
                            <Polozka key={`r${r.id}`} isDark={isDark} nahled={<Nahled recept={r} velikost={38} />} nazev={r.nazev}
                                popisek={r.nalezeno_v ? `Nalezeno v surovinách: ${r.nalezeno_v}` : r.kategorie?.nazev}
                                onClick={() => jdi({ druh: 'recepty', recept: r.id })} />
                        ))}
                    </>}
                </div>
            ) : <>
                <div className="grid grid-cols-2 gap-2" style={{ marginTop: 14 }}>
                    {prehled.druhy.map(d => {
                        const cfg = DRUHY[d.kod];
                        if (!cfg) return null;
                        const cekani = typeof cfg.cekani === 'function' ? cfg.cekani(d.k_potvrzeni) : cfg.cekani;
                        return (
                            <button key={d.kod} onClick={() => jdi({ druh: d.kod })} className="text-left"
                                style={{ ...s.karta, borderRadius: 14, padding: 14, cursor: 'pointer' }}>
                                <div className="flex items-center justify-center" style={{ width: 38, height: 38, borderRadius: 11, background: `color-mix(in srgb, ${cfg.barva} 12%, transparent)`, color: cfg.barva }}>
                                    <cfg.Icon size={18} />
                                </div>
                                <div className="text-[15px] font-bold" style={{ color: 'var(--text-primary)', marginTop: 10 }}>{cfg.nazev}</div>
                                <div className="text-[11px]" style={{ color: 'var(--text-muted)', marginTop: 2 }}>{sklonuj(d.pocet, cfg.jednotky)}</div>
                                {d.k_potvrzeni > 0 && (
                                    <span className="inline-block text-[10.5px] font-bold rounded-full" style={{ marginTop: 8, padding: '3px 8px', background: 'rgba(245,158,11,0.14)', color: '#FBBF24' }}>
                                        {d.k_potvrzeni} {cekani}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {prehled.k_potvrzeni.length > 0 && <>
                    <Nadpis>K potvrzení</Nadpis>
                    {prehled.k_potvrzeni.map(x => {
                        const cfg = DRUHY[x.druh];
                        return (
                            <Polozka key={`${x.druh}${x.id}`} isDark={isDark} Icon={cfg.Icon} barva={cfg.barva} nazev={x.nazev} popisek={x.popisek} barevnyPopisek
                                onClick={() => jdi(x.druh === 'smernice' ? { druh: 'smernice', smernice: x.id } : { druh: 'recepty', recept: x.id })} />
                        );
                    })}
                </>}

                {prehled.k_potvrzeni.length === 0 && (
                    <p className="text-xs text-center" style={{ color: 'var(--text-muted)', marginTop: 18 }}>Všechno máš potvrzené.</p>
                )}

                {posledni.length > 0 && <>
                    <Nadpis>Naposledy otevřené</Nadpis>
                    {posledni.map(r => (
                        <Polozka key={`n${r.id}`} isDark={isDark} nahled={<Nahled recept={r} velikost={38} />} nazev={r.nazev}
                            onClick={() => jdi({ druh: 'recepty', recept: r.id })} />
                    ))}
                </>}
            </>}
        </Posun>
    );
}

function Polozka({ isDark, Icon, barva, nahled, nazev, popisek, barevnyPopisek, onClick }) {
    const s = styly(isDark);
    return (
        <button onClick={onClick} className="w-full flex items-center gap-3 text-left"
            style={{ ...s.karta, padding: nahled ? '8px 12px' : 12, marginBottom: 8, cursor: 'pointer' }}>
            {nahled || (
                <div className="shrink-0 flex items-center justify-center" style={{ width: 38, height: 38, borderRadius: 11, background: `color-mix(in srgb, ${barva} 12%, transparent)`, color: barva }}>
                    <Icon size={18} />
                </div>
            )}
            <div className="min-w-0 flex-1">
                {popisek && barevnyPopisek && <div className="text-[11px] font-semibold truncate" style={{ color: barva }}>{popisek}</div>}
                <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{nazev}</div>
                {popisek && !barevnyPopisek && <div className="text-[11px] truncate" style={{ color: 'var(--text-muted)', marginTop: 1 }}>{popisek}</div>}
            </div>
            <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        </button>
    );
}
